import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { connect } from "react-redux";
import moment from "moment";
import DashboardDatePicker from "../DashboardDatePicker";
import ChartViewToggle from "../ChartViewToggle";
import ChartCard, { ChartLegend, ChartTypeToggle, FilterSelect, ResetButton } from "../ChartCard";
import useChartRequest from "../useChartRequest";
import { PALETTE, cartesianOptions, formatNumber, isAllZero, seriesDataset } from "../chartTheme";
import { sendPostRequest } from "../../../../../actions/app-actions";
import { getAdminBusinessId } from "../../../../../utils/authHelper";

const SERIES = [
  { key: "scheduled", label: "Scheduled", color: PALETTE.brand },
  { key: "completed", label: "Completed", color: PALETTE.success },
  { key: "cancelled", label: "Cancelled", color: PALETTE.danger },
  { key: "noshow", label: "No-show", color: PALETTE.warning },
];

const GlobalChart = (props) => {
  const { rows, loading, refreshing, error, load } = useChartRequest(
    props.sendPostRequest,
    `/admin/event-stats-chart/${getAdminBusinessId()}`
  );
  const [chartType, setChartType] = useState("bar");
  const [period, setPeriod] = useState(new Date());
  const [isMonthly, setIsMonthly] = useState(false);
  const [eventId, setEventId] = useState("");

  const request = (next = {}) => {
    const p = next.period !== undefined ? next.period : period;
    const monthly = next.isMonthly !== undefined ? next.isMonthly : isMonthly;
    const ev = next.eventId !== undefined ? next.eventId : eventId;
    load({
      event_id: ev,
      is_month_view: monthly ? "E" : "D",
      month: moment(p).format("MM"),
      year: moment(p).format("YYYY"),
    });
  };

  useEffect(() => {
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(
    () =>
      SERIES.reduce((acc, s) => {
        acc[s.key] = rows.reduce((sum, r) => sum + (Number(r[s.key]) || 0), 0);
        return acc;
      }, {}),
    [rows]
  );

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.index),
      datasets: SERIES.map((s) =>
        seriesDataset({ label: s.label, data: rows.map((r) => r[s.key]), color: s.color, type: chartType })
      ),
    }),
    [rows, chartType]
  );

  const options = useMemo(() => cartesianOptions(), []);
  const empty = isAllZero(...SERIES.map((s) => rows.map((r) => r[s.key])));
  const periodLabel = isMonthly ? moment(period).format("YYYY") : moment(period).format("MMMM YYYY");

  const actions = (
    <>
      <FilterSelect
        value={eventId}
        options={props.events}
        placeholder="All events"
        onChange={(v) => {
          setEventId(v);
          request({ eventId: v });
        }}
      />
      <DashboardDatePicker
        mode={isMonthly ? "year" : "month"}
        value={period}
        onChange={(val) => {
          const next = val || new Date();
          setPeriod(next);
          request({ period: next });
        }}
      />
      <ChartViewToggle
        leftLabel="Daily"
        rightLabel="Monthly"
        isRightActive={isMonthly}
        onChange={(monthly) => {
          setIsMonthly(monthly);
          request({ isMonthly: monthly });
        }}
      />
      <ChartTypeToggle value={chartType} onChange={setChartType} />
      <ResetButton
        onClick={() => {
          const now = new Date();
          setEventId("");
          setIsMonthly(false);
          setPeriod(now);
          request({ eventId: "", isMonthly: false, period: now });
        }}
      />
    </>
  );

  return (
    <ChartCard
      title="Booking performance"
      subtitle={`Bookings by status · ${periodLabel}`}
      tooltipCode="P3F1"
      actions={actions}
      summary={
        <ChartLegend items={SERIES.map((s) => ({ label: s.label, color: s.color, value: formatNumber(totals[s.key]) }))} />
      }
      loading={loading}
      refreshing={refreshing}
      error={error}
      empty={empty}
      emptyTitle="No bookings in this period"
      height={320}
    >
      {chartType === "bar" ? (
        <Bar data={chartData} options={options} />
      ) : (
        <Line data={chartData} options={options} />
      )}
    </ChartCard>
  );
};

export default connect(null, { sendPostRequest })(GlobalChart);
